import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

type CallbackStatus = 'loading' | 'success' | 'error';

export function GoogleCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackStatus>('loading');
  const [message, setMessage] = useState('Connecting your Google account...');

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setMessage(`Authorization failed: ${error}`);
        setTimeout(() => navigate('/settings'), 3000);
        return;
      }

      if (!code) {
        setStatus('error');
        setMessage('No authorization code received');
        setTimeout(() => navigate('/settings'), 3000);
        return;
      }

      try {
        const result = await api.completeGoogleAuth(code);
        
        if (result.success) {
          setStatus('success');
          setMessage('Google account connected successfully!');
        } else {
          setStatus('error');
          setMessage('Failed to connect Google account');
        }
      } catch (err) {
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Failed to connect Google account');
      }

      // Redirect to settings after a short delay
      setTimeout(() => navigate('/settings'), 2000);
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <div className="text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-accent" />
            <p className="mt-4 text-lg text-text-secondary">{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
            <p className="mt-4 text-lg text-success">{message}</p>
            <p className="mt-2 text-sm text-text-tertiary">Redirecting to settings...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="mx-auto h-12 w-12 text-error" />
            <p className="mt-4 text-lg text-error">{message}</p>
            <p className="mt-2 text-sm text-text-tertiary">Redirecting to settings...</p>
          </>
        )}
      </div>
    </div>
  );
}
